import React, { useEffect, useState } from 'react';
import { safeFetchJSON } from '../utils/apiHelpers';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import useSafeBackNavigation from '../hooks/useSafeBackNavigation';
import { safeFetch } from '../utils/api';

import { getBackendUrl, getApiUrl } from '../config/environment';
const BACKEND_URL = getBackendUrl();

function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const handleBack = useSafeBackNavigation('/training');

  const MAX_ATTEMPTS = 3;
  const POLL_INTERVAL = 3000; // 3 seconds

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const checkPaymentStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await safeFetch(`${BACKEND_URL}/api/billing/checkout-status/${sessionId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error('Erreur lors de la vérification du paiement');
        }

        const data = await response.json();

        if (data.payment_status === 'paid') {
          setPaymentDetails(data);
          setStatus('success');

          // Update user context with new plan
          if (user) {
            setUser({
              ...user,
              plan: 'premium',
              subscription_status: 'trialing'
            });
          }

          // 🛟 Iter339 — Si l'utilisateur venait d'un checkout "credits_exhausted_checkout"
          //    déclenché depuis une session Owen en cours, on le repositionne sur la
          //    page d'échange EXACTEMENT où il en était (pathname + query).
          //    L'helper TTL=30min protège contre les snapshots périmés.
          try {
            // eslint-disable-next-line global-require
            const { loadResumeState, clearResumeState, getResumeUrl } = await import('../utils/sessionResume');
            const snap = loadResumeState();
            if (snap && snap.reason === 'credits_exhausted_checkout') {
              const url = getResumeUrl(snap);
              clearResumeState();
              if (url) {
                // Petite latence pour laisser le webhook Stripe mettre à jour le solde
                setTimeout(() => navigate(url, { replace: true }), 1200);
              }
            }
          } catch (_) { /* noop, on reste sur la page success par défaut */ }
        } else if (data.status === 'expired') {
          setStatus('error');
        } else if (attempts < MAX_ATTEMPTS) {
          // Continue polling
          setAttempts(attempts + 1);
          setTimeout(checkPaymentStatus, POLL_INTERVAL);
        } else {
          // Max attempts reached - assume webhook will handle it, redirect to success
          setStatus('success');
          // P0 FIX: Ne pas hardcoder le montant, laisser null pour ne pas afficher
          setPaymentDetails(null);
        }
      } catch (error) {
        console.error('Erreur:', error);
        if (attempts < MAX_ATTEMPTS) {
          setAttempts(attempts + 1);
          setTimeout(checkPaymentStatus, POLL_INTERVAL);
        } else {
          // Max attempts reached - assume webhook will handle it, redirect to success
          setStatus('success');
          // P0 FIX: Ne pas hardcoder le montant, laisser null pour ne pas afficher
          setPaymentDetails(null);
        }
      }
    };

    checkPaymentStatus();
  }, [searchParams, attempts, user, setUser]);

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-amber-400 mb-4"></div>
          <h2 className="text-2xl font-bold text-white mb-2">Vérification du paiement...</h2>
          <p className="text-gray-400">Veuillez patienter quelques instants</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20 text-center">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Erreur de vérification</h2>
          <p className="text-gray-300 mb-6">
            Nous n'avons pas pu vérifier votre paiement. Veuillez vérifier votre email pour la confirmation.
          </p>
          <button
            onClick={handleBack}
            className="bg-amber-500 hover:bg-amber-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
          >
            Retour au tableau de bord
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20 text-center">
        {/* Success Icon */}
        <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-10 h-10 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>

        <h1 className="text-3xl font-bold text-white mb-2">
          Paiement Réussi ! 🎉
        </h1>
        <p className="text-gray-300 mb-6">
          Bienvenue dans le plan Premium du Coach DIA
        </p>

        {/* Payment Details */}
        {paymentDetails && (
          <div className="bg-white/5 rounded-xl p-4 mb-6 text-left">
            <div className="flex justify-between items-center mb-2">
              <span className="text-gray-400">Montant</span>
              <span className="text-white font-semibold">
                {(paymentDetails.amount_total / 100).toFixed(2)} {paymentDetails.currency.toUpperCase()}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Statut</span>
              <span className="text-green-400 font-semibold">✓ Payé</span>
            </div>
          </div>
        )}

        {/* Benefits - P0 FIX: Texte cohérent avec modèle économique sessions */}
        <div className="text-left mb-6 space-y-2">
          <h3 className="text-white font-semibold mb-3">Vous avez maintenant accès à :</h3>
          <div className="flex items-center space-x-2 text-gray-300">
            <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span>Sessions de coaching IA personnalisées</span>
          </div>
          <div className="flex items-center space-x-2 text-gray-300">
            <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span>Tous les modules de formation premium</span>
          </div>
          <div className="flex items-center space-x-2 text-gray-300">
            <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span>Scénarios IA immersifs</span>
          </div>
        </div>

        <button
          onClick={handleBack}
          className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-bold py-3 px-6 rounded-xl transition-all duration-200 transform hover:scale-105"
        >
          Commencer la formation
        </button>
      </div>
    </div>
  );
}

export default PaymentSuccess;
